import React from 'react';
import { Component } from 'react';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { getMediaClient } from '@atlaskit/media-client-react';
import type { FileState, MediaSubscribable } from '@atlaskit/media-client';
import type { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
// eslint-disable-next-line
import 'rxjs/add/observable/zip';
import { fetchToken } from './domain/fetch-token';
import { MediaFileAttributesFactory } from './domain/media-file-attributes';
import type {
	MediaViewerConstructor,
	MediaViewerInterface,
	MediaViewerConfig,
} from './mediaviewer';
import type { MediaViewerItem } from './media-viewer';
import { generatePreview, isPreviewGenerated } from './domain/preview';

export interface MediaFileListViewerProps {
	readonly mediaClientConfig: MediaClientConfig;

	readonly selectedItem: MediaViewerItem;
	readonly list: Array<MediaViewerItem>;

	readonly collectionName?: string;

	readonly MediaViewer: MediaViewerConstructor;
	readonly mediaViewerConfiguration?: MediaViewerConfig;
	readonly basePath: string;

	readonly onClose?: () => void;
}

export interface MediaListFileViewerState {
	readonly subscription?: Subscription;
	readonly mediaViewer: MediaViewerInterface;
}

export class MediaFileListViewer extends Component<
	MediaFileListViewerProps,
	MediaListFileViewerState
> {
	constructor(props: MediaFileListViewerProps) {
		super(props);

		const { mediaClientConfig, collectionName, basePath, MediaViewer, mediaViewerConfiguration } =
			props;

		const config = {
			...mediaViewerConfiguration,
			isPreviewGenerated: isPreviewGenerated(MediaViewer),
			generatePreview: generatePreview(MediaViewer),
		};

		const { authProvider } = mediaClientConfig;

		this.state = {
			mediaViewer: new MediaViewer({
				...config,
				assets: {
					basePath,
				},
				fetchToken: fetchToken(authProvider, collectionName),
			}),
		};
	}

	componentDidMount(): void {
		const { mediaClientConfig, selectedItem, list, collectionName } = this.props;
		const { authProvider } = mediaClientConfig;
		const { mediaViewer } = this.state;

		mediaViewer.on('fv.close', this.onClose);

		const filesToProcess = list.filter((item) => item.type === 'file'); // for now we only support files

		const mediaClient = getMediaClient(mediaClientConfig);
		const fileStateObservables: MediaSubscribable[] = filesToProcess.map((file: MediaViewerItem) =>
			mediaClient.file.getFileState(file.id, {
				collectionName,
				occurrenceKey: file.occurrenceKey,
			}),
		);

		// TODO: needs to be fixed
		// eslint-disable-next-line react/no-direct-mutation-state
		this.state = {
			subscription: Observable.zip(...fileStateObservables).subscribe({
				next: (fileStates: FileState[]) => {
					void authProvider().then(({ baseUrl }) => {
						const files = MediaFileAttributesFactory.fromFileItemList(fileStates, baseUrl);
						mediaViewer.setFiles(files);
						const selectedFileState = fileStates.find(
							({ id, occurrenceKey }) =>
								id === selectedItem.id &&
								((!occurrenceKey && !selectedItem.occurrenceKey) ||
									occurrenceKey === selectedItem.occurrenceKey),
						);

						const id = MediaFileAttributesFactory.getUniqueMediaViewerId(
							selectedFileState || fileStates[0],
						);
						void mediaViewer.open({ id });
					});
				},
			}),
			mediaViewer,
		};
	}

	componentWillUnmount(): void {
		const { mediaViewer, subscription } = this.state;
		if (subscription) {
			subscription.unsubscribe();
		}
		mediaViewer.off('fv.close', this.onClose);
	}

	render(): JSX.Element {
		return <div />;
	}

	private onClose = () => {
		const { onClose } = this.props;
		const { subscription } = this.state;
		if (subscription) {
			subscription.unsubscribe();
		}
		if (onClose) {
			onClose();
		}
	};
}
